// The day as a vertical timeline — every event from the morning's first down
// through now into the upcoming predictions. Logged events show solid; upcoming
// ones show muted/dashed. Logging anything makes it appear here in place.

import { hmShort, durStr, minutesFromMidnight } from "../lib/timeFmt";
import { startOfLocalDay, nursingOzEstimate } from "../lib/events";
import type { EventRow } from "../lib/events";
import type { DayPlan } from "../lib/dayPlan";
import { EventIcon, IconChevron } from "./icons";
import { useT, useAmount } from "../lib/LocaleContext";
import type { Strings } from "../lib/i18n";

type AmountFmt = ReturnType<typeof useAmount>;

type RowKind = "feed" | "nurse" | "sleep" | "bedtime" | "diaper";
type RowStatus = "logged" | "live" | "upcoming";

type Row = {
  id: string;
  startMin: number;
  kind: RowKind;
  status: RowStatus;
  title: string;
  detail: string;
  /** The underlying logged event, when this row is editable. */
  event?: EventRow;
};

type Props = {
  plan: DayPlan;
  events: EventRow[];
  onFullDay?: () => void;
  onEditEvent: (event: EventRow) => void;
};

export function DayTimeline({ plan, events, onFullDay, onEditEvent }: Props) {
  const t = useT();
  const amount = useAmount();
  const rows = [...planRows(plan, t, amount), ...diaperRows(events, t)].sort((a, b) => a.startMin - b.startMin);
  const nowIdx = rows.findIndex((r) => r.startMin > plan.nowMin);

  return (
    <div style={styles.wrap}>
      <div style={styles.head}>
        <span style={styles.headLabel}>{t.dayTimeline.today}</span>
        {onFullDay && (
          <button style={styles.headLink} onClick={onFullDay}>
            {t.dayTimeline.history} <IconChevron size={12} dir="right" />
          </button>
        )}
      </div>
      <div style={styles.list}>
        {rows.length === 0 && <div style={styles.empty}>{t.dayTimeline.empty}</div>}
        {rows.map((r, i) => (
          <div key={`row-${r.id}`}>
            {i === nowIdx && <NowDivider nowMin={plan.nowMin} />}
            <TimelineRow row={r} onEdit={r.event ? () => onEditEvent(r.event!) : undefined} />
          </div>
        ))}
        {nowIdx === -1 && rows.length > 0 && <NowDivider nowMin={plan.nowMin} />}
      </div>
    </div>
  );
}

function TimelineRow({ row, onEdit }: { row: Row; onEdit?: () => void }) {
  const color =
    row.kind === "feed" || row.kind === "nurse"
      ? "var(--bh-feed)"
      : row.kind === "sleep" || row.kind === "bedtime"
        ? "var(--bh-sleep)"
        : "var(--bh-ink-3)";
  const soft =
    row.kind === "feed" || row.kind === "nurse"
      ? "var(--bh-feed-soft)"
      : row.kind === "sleep" || row.kind === "bedtime"
        ? "var(--bh-sleep-soft)"
        : "var(--bh-surface-2)";
  const upcoming = row.status === "upcoming";

  const inner = (
    <>
      <span className="bh-num" style={styles.time}>
        {hmShort(row.startMin)}
      </span>
      <div
        style={{
          ...styles.dot,
          color,
          background: upcoming ? "transparent" : soft,
          border: upcoming ? `1.5px dashed ${color}` : "none",
        }}
      >
        <EventIcon kind={iconKind(row.kind)} size={14} />
      </div>
      <span style={styles.title}>{row.title}</span>
      {row.status === "live" && <span style={styles.liveDot} />}
      <span style={{ flex: 1 }} />
      <span className="bh-num" style={styles.detail}>
        {row.detail}
      </span>
    </>
  );

  if (onEdit) {
    return (
      <button style={{ ...styles.row, ...styles.rowButton }} onClick={onEdit}>
        {inner}
      </button>
    );
  }
  return <div style={{ ...styles.row, opacity: upcoming ? 0.6 : 1 }}>{inner}</div>;
}

function NowDivider({ nowMin }: { nowMin: number }) {
  const t = useT();
  return (
    <div style={styles.nowRow}>
      <span style={styles.nowLine} />
      <span style={styles.nowLabel}>{t.dayTimeline.now}</span>
      <span className="bh-num" style={styles.nowTime}>
        {hmShort(nowMin)}
      </span>
      <span style={styles.nowLine} />
    </div>
  );
}

function iconKind(k: RowKind): "feed" | "nurse" | "sleep" | "bedtime" | "diaper" {
  return k;
}

function planRows(plan: DayPlan, t: Strings, amount: AmountFmt): Row[] {
  const rows: Row[] = [];
  for (const e of plan.events) {
    if (e.kind === "wake") continue;
    if (e.kind === "feed" || e.kind === "nurse") {
      let detail = "";
      if (e.status === "upcoming") {
        const p = e.predicted;
        detail = p?.amountRange
          ? t.dayTimeline.detailRangeOz(amount.range(p.amountRange[0], p.amountRange[1]))
          : p?.amountOz != null
            ? t.dayTimeline.detailApproxOz(amount.fmt(p.amountOz))
            : t.dayTimeline.detailPredicted;
      } else if (e.kind === "nurse" && e.durationMin != null) {
        detail = t.dayTimeline.detailNursed(e.durationMin, amount.fmt(nursingOzEstimate(e.durationMin)));
      } else {
        detail = e.amountOz != null
          ? t.dayTimeline.detailOz(amount.fmt(e.amountOz))
          : e.durationMin != null
            ? t.dayTimeline.detailMin(e.durationMin)
            : "";
      }
      rows.push({
        id: e.id,
        startMin: e.start,
        kind: e.kind,
        status: e.status,
        title: e.status === "upcoming" ? t.dayTimeline.titleFeed : e.kind === "nurse" ? t.dayTimeline.titleNursed : t.dayTimeline.titleFed,
        detail,
        event: e.row,
      });
    } else if (e.kind === "sleep") {
      let title = t.dayTimeline.titleNap;
      let detail = "";
      if (e.status === "live") {
        title = t.dayTimeline.titleSleeping;
        detail = t.dayTimeline.detailInProgress;
      } else if (e.status === "upcoming") {
        detail = t.dayTimeline.detailApproxMin(e.predicted?.dur ?? 75);
      } else {
        // Full duration across midnight — `end - start` is clipped to this day,
        // so a 7-hour night sleep would otherwise read as the 20 min before
        // midnight (or the 6 hours after it).
        detail = durStr(e.totalDurationMin ?? e.durationMin ?? Math.max(1, (e.end ?? e.start) - e.start));
      }
      if (e.continuedFromPrevDay && e.status !== "live") title = t.dayTimeline.titleNightSleep;
      rows.push({ id: e.id, startMin: e.start, kind: "sleep", status: e.status, title, detail, event: e.row });
    } else if (e.kind === "bedtime") {
      rows.push({
        id: e.id,
        startMin: e.start,
        kind: "bedtime",
        status: e.status,
        title: t.dayTimeline.titleBedtime,
        detail: t.dayTimeline.detailBedtimeRoutine,
      });
    }
  }
  return rows;
}

function diaperRows(events: EventRow[], t: Strings): Row[] {
  const dayStart = startOfLocalDay().getTime();
  return events
    .filter((e) => e.type === "diaper" && new Date(e.at).getTime() >= dayStart)
    .map((e) => ({
      id: e.id,
      startMin: minutesFromMidnight(new Date(e.at)),
      kind: "diaper" as const,
      status: "logged" as const,
      title: t.dayTimeline.titleDiaper,
      detail: e.diaper_kind === "both" ? t.dayTimeline.diaperBoth : (e.diaper_kind ?? ""),
      event: e,
    }));
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { margin: "8px 14px 0" },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 4px",
    marginBottom: 10,
  },
  headLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
  },
  headLink: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--bh-ink-3)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  list: { background: "var(--bh-surface)", borderRadius: 20, padding: "6px 16px" },
  empty: { fontSize: 14, color: "var(--bh-ink-3)", padding: "16px 0", textAlign: "center" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "11px 0",
    minHeight: 52,
  },
  rowButton: {
    width: "100%",
    appearance: "none",
    background: "transparent",
    border: "none",
    font: "inherit",
    color: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
  time: { width: 52, fontSize: 14, color: "var(--bh-ink-3)", flexShrink: 0 },
  dot: {
    width: 30,
    height: 30,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box",
  },
  title: { fontSize: 16, color: "var(--bh-ink)", fontWeight: 500 },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    background: "var(--bh-sleep)",
    marginLeft: 6,
    animation: "bh-pulse 1.6s ease-in-out infinite",
  },
  detail: { fontSize: 14, color: "var(--bh-ink-3)" },
  nowRow: { display: "flex", alignItems: "center", gap: 8, padding: "6px 0" },
  nowLine: { flex: 1, height: 1, background: "var(--bh-now)", opacity: 0.5 },
  nowLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "var(--bh-now)",
  },
  nowTime: { fontSize: 11, color: "var(--bh-now)" },
};
